import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import set from 'lodash.set';
import get from 'lodash.get';
import StepsContext from '../providers/Steps/Steps.context';
import { useMatch } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';
import { FormApi } from 'final-form';
import PopNotificationsContext from '../providers/PopNotifications/PopNotifications.context';
import { safeParse, safeStringify } from 'utils';
import { EDIT_ROUTE } from 'data/global';

type FormPatchingProps<PatchData, PatchObj, Name extends string> = {
  readonly name: Name;
  patchCondition?: boolean;
  patchOnEdit?: boolean;
  initRequest?: () => Promise<
    AxiosResponse<{
      [key in Name]: PatchObj;
    }>
  >;
  patchRequest: (patchValues: Partial<PatchData>) => any;
  setLoading?: (loading?: boolean) => void;
  onSubmit: (values: PatchData) => void;
  onForbiddenRequest?: () => void;
  replace?: boolean;
  withRouting?: boolean;
};

const beFields = [
  'id',
  '_id',
  '_v',
  'createdAt',
  'updatedAt',
  'isApproved',
  'isSubmitted',
  'status',
  'isPublished',
];

export default function useFormPatching<
  PatchData extends Record<string, any>,
  PatchObj extends Record<string, any>,
  Name extends string,
>({
  patchCondition = true,
  patchOnEdit = true,
  onSubmit,
  setLoading,
  initRequest,
  patchRequest,
  onForbiddenRequest,
  name,
  replace,
  withRouting = true,
}: FormPatchingProps<PatchData, PatchObj, Name>) {
  const { popServerError } = useContext(PopNotificationsContext);
  const initialized = useRef(false);
  const {
    currentStep,
    steps,
    steps: { length: stepsNumber },
    nextStep,
    finishSubmit,
  } = useContext(StepsContext);

  const activePatchStep = useMemo(
    () => steps[currentStep],
    [steps, currentStep],
  );

  const isEdit = useMatch(EDIT_ROUTE);

  const [initialData, setInitialData] = useState<Partial<PatchObj>>({});
  const [loadingInitialData, setLoadingInitialData] = useState(false);

  const mNextStep = useCallback(
    (values: Record<string, any>) => {
      setInitialData((old) => ({ ...old, ...values }));
      nextStep({ replace });
    },
    [nextStep, replace],
  );

  useEffect(() => {
    if (!initRequest || initialized.current) return;

    const getPatchedData = async () => {
      try {
        setLoadingInitialData(true);
        const { data } = await initRequest();
        setInitialData(data[name]);
      } catch (er) {
        popServerError(er);
        if ((er as AxiosError).response?.status === 403) {
          onForbiddenRequest?.();
        }
      } finally {
        setLoadingInitialData(false);
        initialized.current = true;
      }
    };

    getPatchedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, popServerError, initRequest]);

  const onStepSubmit = useCallback(
    async (values: Partial<PatchData>, form: FormApi<any>) => {
      if ((currentStep === stepsNumber - 1 || finishSubmit) && patchCondition) {
        await onSubmit(values as PatchData);
        return;
      }

      const stepFieldKeys = Object.keys(form.getState().modified);

      const valuesFromThisStep = {};
      stepFieldKeys.forEach((key) =>
        set(valuesFromThisStep, key, get(values, key)),
      );

      if (activePatchStep?.skipPatch) {
        withRouting && mNextStep(valuesFromThisStep);
        return;
      }

      if (currentStep >= 0 && patchCondition) {
        try {
          setLoading?.(true);

          if (!isEdit || patchOnEdit) await patchRequest(valuesFromThisStep);

          withRouting && mNextStep(valuesFromThisStep);
        } catch (er) {
          popServerError(er);
        } finally {
          setLoading?.(false);
        }
        return;
      }

      const filteredValues = safeParse(safeStringify(values));

      beFields.forEach((key) => delete filteredValues[key]);
    },
    [
      currentStep,
      stepsNumber,
      finishSubmit,
      patchCondition,
      activePatchStep?.skipPatch,
      onSubmit,
      withRouting,
      mNextStep,
      setLoading,
      isEdit,
      patchOnEdit,
      patchRequest,
      popServerError,
    ],
  );

  return {
    initialData,
    loadingInitialData,
    onStepSubmit,
  } as const;
}
